import { configuracaoFaltando } from './lib/supabase.js';
import { emDemonstracao, sairDaDemonstracao } from './demo/demo.js';
import { carregarSessao, entrar, sair, aoMudarSessao, usuario, ehGestao } from './auth.js';
import { carregarTudo } from './state.js';
import { montarNav, aoAbrir, irPara } from './ui/nav.js';

import * as Dashboard from './views/dashboard.js';
import * as Empresas from './views/empresas.js';
import * as Ficha from './views/ficha.js';
import * as Documentos from './views/documentos.js';
import * as Operacional from './views/operacional.js';
import * as Historico from './views/historico.js';
import * as Estaticos from './views/estaticos.js';
import * as Config from './views/config.js';
import * as Indicadores from './views/indicadores.js';
import * as Cobranca from './views/cobranca.js';

const overlayLogin = document.getElementById('loginOverlay');
const boot = document.getElementById('appBoot');
const appRoot = document.getElementById('appRoot');

let telasMontadas = false;

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

async function iniciar() {
  if (emDemonstracao) {
    await carregarSessao();   // define o usuário fictício antes de renderizar
    await abrirApp();
    return;
  }

  if (configuracaoFaltando) {
    mostrarConfiguracaoPendente();
    return;
  }

  try {
    const colaborador = await carregarSessao();
    if (colaborador) {
      await abrirApp();
    } else {
      mostrarLogin();
    }
  } catch (err) {
    mostrarLogin(err.message);
  }
}

/**
 * Sem VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY não há o que carregar.
 * Em vez de uma tela em branco, explicamos o que fazer.
 */
function mostrarConfiguracaoPendente() {
  overlayLogin.hidden = true;
  appRoot.hidden = true;
  boot.hidden = false;
  boot.innerHTML = `
    <div class="login-card" style="max-width:520px;text-align:left;">
      <h1 class="login-title">Falta configurar a conexão</h1>
      <p class="login-sub">
        O sistema não sabe em qual banco entrar. Isso acontece quando o arquivo
        <code>.env.local</code> não existe ou está incompleto.
      </p>
      <p style="font-size:13px;line-height:1.6;color:var(--text-secondary);">
        <b>Para resolver:</b><br>
        1. Copie o arquivo <code>.env.example</code> para <code>.env.local</code>.<br>
        2. Preencha as duas variáveis com os dados do seu projeto no Supabase
           (painel do Supabase → <i>Project Settings → API</i>).<br>
        3. Pare o servidor e rode <code>npm run dev</code> de novo — o Vite só lê
           esse arquivo na inicialização.
      </p>
      <p style="font-size:12.5px;color:var(--text-muted);">
        Se isto aparecer no site publicado, as variáveis não foram cadastradas no
        Cloudflare Pages em <i>Settings → Variables and Secrets</i>.
      </p>
      <hr style="border:0;border-top:1px solid var(--border);margin:18px 0;">
      <p style="font-size:13px;color:var(--text-secondary);margin-top:0;">
        Só quer conhecer o sistema? Veja a demonstração com dados fictícios,
        sem precisar configurar nada.
      </p>
      <button class="login-btn" type="button" id="btnDemo">Ver demonstração</button>
    </div>`;

  document.getElementById('btnDemo').addEventListener('click', () => {
    location.href = location.pathname + '?demo';
  });
}

/**
 * Faixa fixa no topo, para ninguém confundir a demonstração com o
 * sistema de verdade — os números aqui são todos inventados.
 */
function mostrarFaixaDemo() {
  if (document.getElementById('faixaDemo')) return;

  const faixa = document.createElement('div');
  faixa.id = 'faixaDemo';
  faixa.className = 'faixa-demo';
  faixa.innerHTML = `
    <span><b>Modo demonstração</b> — empresas, valores e prazos são fictícios.
    As alterações que você fizer valem só nesta aba e somem ao recarregar.</span>
    <button type="button" id="btnSairDemo">Sair da demonstração</button>`;
  document.body.prepend(faixa);
  document.getElementById('btnSairDemo').addEventListener('click', sairDaDemonstracao);
}

function mostrarLogin(mensagem) {
  boot.hidden = true;
  appRoot.hidden = true;
  overlayLogin.hidden = false;
  if (mensagem) mostrarErroLogin(mensagem);
  document.getElementById('loginEmail').focus();
}

function mostrarErroLogin(mensagem) {
  const erro = document.getElementById('loginErro');
  erro.textContent = mensagem;
  erro.hidden = false;
}

/**
 * Uma falha na carga já apareceu como "o sistema abriu e nada funciona":
 * `carregarTudo` lançava, e com isso `montarTelas` e `renderTudo` nunca
 * rodavam. Como é `montarTelas` quem liga os botões, a tela ficava viva
 * na aparência e morta no clique, sem dizer uma palavra sobre o motivo.
 * Agora a falha aparece na tela, e com a mensagem do servidor junto.
 */
function mostrarFalhaDeCarga(erro) {
  let aviso = document.getElementById('falhaCarga');
  if (!aviso) {
    aviso = document.createElement('div');
    aviso.id = 'falhaCarga';
    aviso.className = 'faixa-demo faixa-erro';
    document.body.prepend(aviso);
  }
  aviso.innerHTML = `
    <span><b>Não consegui carregar os dados.</b>
    O sistema abriu, mas está vazio — não é que não exista informação.
    <br><small>${String(erro?.message || erro).slice(0, 300)}</small></span>
    <button type="button" id="btnTentarDeNovo">Tentar de novo</button>`;
  document.getElementById('btnTentarDeNovo').addEventListener('click', () => {
    aviso.remove();
    abrirApp();
  });
}

async function abrirApp() {
  boot.hidden = true;
  overlayLogin.hidden = true;
  appRoot.hidden = false;
  if (emDemonstracao) mostrarFaixaDemo();

  try {
    await carregarTudo();
  } catch (erro) {
    console.error('Falha ao carregar os dados:', erro);
    mostrarFalhaDeCarga(erro);
    return;
  }

  if (!telasMontadas) {
    montarTelas();
    telasMontadas = true;
  }

  renderTudo();
}

/* ------------------------------------------------------------------ */
/* Montagem (uma vez só — os listeners não se acumulam)                */
/* ------------------------------------------------------------------ */

function montarTelas() {
  montarNav({ ehGestao: ehGestao() });

  // onChange: quem já chamou recarregarFechamento() só precisa redesenhar.
  // Recarregar TUDO depois de cada clique era o que fazia o tráfego explodir
  // com 170 empresas. Só quem cria empresa nova precisa da carga completa.
  Dashboard.montarChrome({ aoTrocarVisao: () => {} });
  Ficha.configurarFicha({ onChange: renderTudo });
  Empresas.configurar({ onChange: recarregar });
  Documentos.configurar({ onChange: renderTudo });
  Operacional.configurar({ onChange: renderTudo });
  Operacional.configurarRealizado();
  Historico.configurar();
  Estaticos.configurar();
  Config.configurar({ onChange: renderTudo });
  Indicadores.configurar();
  Cobranca.configurar({ onChange: renderTudo });

  // O histórico busca no banco toda vez que a aba é aberta, porque os
  // colegas podem ter mexido em alguma coisa enquanto a tela estava aberta.
  aoAbrir('historico', () => Historico.render());
  aoAbrir('documentos', () => { Documentos.render(); Cobranca.render(); });
  aoAbrir('config', () => Config.render());
  // Os indicadores somam no banco; só calcula quando a aba é aberta.
  aoAbrir('relatorios', () => Indicadores.render());

  document.getElementById('logoutBtn').addEventListener('click', async () => {
    await sair();
    location.reload();
  });

  aoMudarSessao(() => location.reload());
}

/* ------------------------------------------------------------------ */
/* Render                                                              */
/* ------------------------------------------------------------------ */

function renderTudo() {
  Dashboard.render();
  Empresas.render();
  // (a tela de documentos agora é por mês; não há mais seletor de empresa)
  Documentos.render();
  Operacional.renderTarefas();
  Operacional.renderPesquisas();
  Operacional.renderConhecimento();
  Operacional.renderRealizado();
  Historico.renderObservacoes();
  Estaticos.renderRelatorios();   // Balancete e Composição estão ocultos
  Config.render();
}

/** Recarrega os dados do Supabase e redesenha — usado após qualquer escrita. */
async function recarregar() {
  await carregarTudo();
  renderTudo();
}

/* ------------------------------------------------------------------ */
/* Login                                                               */
/* ------------------------------------------------------------------ */

document.getElementById('loginForm').addEventListener('submit', async (ev) => {
  ev.preventDefault();
  const botao = document.getElementById('loginBtn');
  const erro = document.getElementById('loginErro');
  erro.hidden = true;
  botao.disabled = true;
  botao.textContent = 'Entrando…';

  try {
    await entrar(
      document.getElementById('loginEmail').value,
      document.getElementById('loginSenha').value
    );
    document.getElementById('loginSenha').value = '';
    await abrirApp();
  } catch (err) {
    mostrarErroLogin(err.message);
  } finally {
    botao.disabled = false;
    botao.textContent = 'Entrar';
  }
});

iniciar();
